import axios from "axios";
import { auth } from "@/config/host.config";

/**
 * Супервизор связности с сервером.
 *
 * Приложение может работать сутками (киоск/каюта): сервер надолго пропадает,
 * вкладка браузера замораживается и интервалы перестают тикать.
 * Модуль отслеживает доступность бэкенда и при восстановлении связи
 * оповещает подписчиков (onServerRestored), чтобы те обновили данные
 * и пересоздали WS-соединение.
 *
 * Источники сигналов:
 *  - window online/offline;
 *  - visibilitychange / pageshow (вкладка «проснулась»);
 *  - успешные ответы axios-интерсептора (notifyServerAlive);
 *  - периодический health-check: в фоне раз в минуту,
 *    при недоступности — с экспоненциальным бэкоффом 1с..60с.
 */

type Listener = () => void;

const IDLE_CHECK_MS = 60000;
const MIN_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 60000;
const REQUEST_TIMEOUT_MS = 8000;
// Если связи «не было» дольше этого порога — считаем данные устаревшими
// и оповещаем подписчиков даже без перехода down→up (случай заморозки вкладки).
const STALE_MS = 300000;

const listeners = new Set<Listener>();

let supervisorStarted = false;
let checkInProgress = false;
let checkTimer: ReturnType<typeof setTimeout> | null = null;
let idleTimer: ReturnType<typeof setTimeout> | null = null;
let backoffAttempts = 0;
let lastAliveAt = Date.now();
let networkOnline = true;
let serverUp = true;

export function isServerReachable(): boolean {
  return networkOnline && serverUp;
}

export function onServerRestored(cb: Listener): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/**
 * Вызывается axios-интерсептором при любом успешном ответе сервера:
 * значит сеть и бэкенд живы — можно мгновенно выйти из бэкофф-цикла.
 */
export function notifyServerAlive(): void {
  lastAliveAt = Date.now();
  if (networkOnline && serverUp) return;

  networkOnline = true;
  serverUp = true;
  backoffAttempts = 0;
  stopTimers();
  scheduleIdleCheck();
  emitRestored();
}

function emitRestored(): void {
  Array.from(listeners).forEach((cb) => cb());
}

function probeServer(): Promise<boolean> {
  if (!navigator.onLine) return Promise.resolve(false);
  return axios
    .post(
      auth.access,
      { status: "Online", path: "connectivity" },
      { timeout: REQUEST_TIMEOUT_MS }
    )
    .then(() => true)
    .catch((error: { response?: { status?: number } }) => {
      const status = error?.response?.status;
      // HTTP-ответ получен и не 5xx — сервер жив (401/403 тоже «жив»)
      return typeof status === "number" && status > 0 && status < 500;
    });
}

async function runHealthCheck(): Promise<void> {
  if (checkInProgress) return;
  checkInProgress = true;
  try {
    const alive = await probeServer();
    if (alive) {
      markAlive();
    } else {
      serverUp = false;
      backoffAttempts += 1;
      stopTimers();
      scheduleCheck();
    }
  } finally {
    checkInProgress = false;
  }
}

function markAlive(): void {
  const wasDown = !serverUp || !networkOnline;
  const wasStale = Date.now() - lastAliveAt > STALE_MS;
  serverUp = true;
  networkOnline = true;
  backoffAttempts = 0;
  lastAliveAt = Date.now();
  stopTimers();
  scheduleIdleCheck();
  if (wasDown || wasStale) emitRestored();
}

function scheduleCheck(): void {
  if (checkTimer) return;
  const delay = Math.min(
    MIN_BACKOFF_MS * 2 ** Math.max(backoffAttempts - 1, 0),
    MAX_BACKOFF_MS
  );
  checkTimer = setTimeout(() => {
    checkTimer = null;
    runHealthCheck();
  }, delay);
}

function scheduleIdleCheck(): void {
  if (idleTimer) return;
  idleTimer = setTimeout(() => {
    idleTimer = null;
    runHealthCheck();
  }, IDLE_CHECK_MS);
}

function stopTimers(): void {
  if (checkTimer) {
    clearTimeout(checkTimer);
    checkTimer = null;
  }
  if (idleTimer) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
}

export function startConnectivitySupervisor(): void {
  if (supervisorStarted) return;
  supervisorStarted = true;

  networkOnline = navigator.onLine;
  lastAliveAt = Date.now();

  window.addEventListener("online", () => {
    networkOnline = true;
    backoffAttempts = 0;
    runHealthCheck();
  });

  window.addEventListener("offline", () => {
    networkOnline = false;
    serverUp = false;
    stopTimers();
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      backoffAttempts = 0;
      runHealthCheck();
    }
  });

  window.addEventListener("pageshow", (event) => {
    if ((event as PageTransitionEvent).persisted) {
      backoffAttempts = 0;
      runHealthCheck();
    }
  });

  runHealthCheck();
}
